import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { type UserResponse } from '@heidi/workspace/contracts';
import { atom, useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useCallback, useRef } from 'react';

export enum PushSectionsToEhrState {
  IDLE = 'idle',
  CONFIRM = 'confirm',
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

export const sectionalWritebackInformModalOpenAtom = atom<{
  open: boolean;
  from: 'template_editor' | 'push_note_to_ehr' | undefined;
}>({
  open: false,
  from: undefined,
});

// toggle sections highlight in template editor
export const sectionalWritebackSectionEnabledOnTemplateEditorAtom = atom<boolean>(
  reactQueryClient.getQueryData<UserResponse>(['getUser'])
    ?.should_show_section_highlight_in_template ?? true
);

// toggle sections highlight in note editor before push to Ehr
export const sectionalWritebackSectionEnabledOnNoteAtom = atom(true);

// push to Ehr button state
export const sectionalWritebackPushToEhrStateAtom = atom<PushSectionsToEhrState>(
  PushSectionsToEhrState.IDLE
);

export const useSectionalWritebackDisplayState = () => {
  const { mutate: updateUser } = useMutateUpdateUser();
  const [
    showSectionsHighlightInTemplateEditor,
    setShowSectionsHighlightInTemplateEditor,
  ] = useAtom(sectionalWritebackSectionEnabledOnTemplateEditorAtom);
  const [showSectionsHighlightInNote, setShowSectionsHighlightInNote] = useAtom(
    sectionalWritebackSectionEnabledOnNoteAtom
  );

  // Create a debounced version of updateUser
  const debouncedUpdateUserHighlightInTemplate = useRef(
    debounce((value: boolean) => {
      updateUser({
        should_show_section_highlight_in_template: value,
      });
    }, 300)
  ).current;

  const toggleSectionHighlightOnTemplateEditor = useCallback(() => {
    setShowSectionsHighlightInTemplateEditor((prev) => {
      const newState = !prev;
      debouncedUpdateUserHighlightInTemplate(newState);
      return newState;
    });
  }, [debouncedUpdateUserHighlightInTemplate, setShowSectionsHighlightInTemplateEditor]);

  return {
    showSectionsHighlightInTemplateEditor,
    toggleSectionHighlightOnTemplateEditor,
    showSectionsHighlightInNote,
    setShowSectionsHighlightInNote,
  };
};
